import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, FileText, Users, PhoneCall } from 'lucide-react';

const CountUp = ({ end, suffix = '', duration = 1000, className }) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    let startTime;
    const step = (timestamp) => {
      if (startTime === undefined) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * end));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [end, duration]);

  return (
    <span className={className}>
      {value}
      {suffix}
    </span>
  );
};

const Home = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <section className="Home_hero__+jh72">
      <div className="container Home_heroContainer__PHOB8">
        <div className="Home_heroContent__MEu7c">
          <motion.span
            className="Home_heroLabel__PdlcS"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            CERTIFIED CAREER ANALYSTS
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Comprehensive Career Counselling by Expert
          </motion.h1>
          <motion.p
            className="Home_heroSubtext__gpLYd"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Explore and Find out your most suitable Career Path out of 20 Career Clusters, 160+ Career Paths and 3000+ Occupations. Vitae Career Enterprises is with you for face to face & online Career Counselling Service under the guidance of "Certified Career Analysts".
          </motion.p>
          <motion.p
            className="Home_heroTagline__YNfl+"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            We believe in "Timely acquired Career guidance gives Success." Do You?
          </motion.p>
          <motion.div
            className="Home_heroButtons__KQY4j"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{ padding: 0 }}
          >
            <Link to="/about" className="btn-ghost" style={{ padding: '0.5rem 1.5rem', fontSize: '0.9rem' }}>
              Learn More About Us
            </Link>
          </motion.div>
        </div>
        <motion.div
          className="Home_heroCard__WKRVj"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2rem' }}
        >
          <a
            href="https://vitaecareer.edumilestones.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ backgroundColor: '#ff6b35', color: '#ffffff', padding: '0.75rem 2rem', fontSize: '1rem' }}
          >
            Explore Your True Career From →
          </a>
          <div className="Home_statList__1yAxI" style={{ width: '100%' }}>
            <div className="Home_statRow__fInza">
              <CountUp end={20} className="Home_statNumber__GYegH" />
              <span className="Home_statLabel__wjI1z">Career Clusters</span>
            </div>
            <div className="Home_statRow__fInza">
              <CountUp end={160} suffix="+" className="Home_statNumber__GYegH" />
              <span className="Home_statLabel__wjI1z">Career Paths</span>
            </div>
            <div className="Home_statRow__fInza">
              <CountUp end={3000} suffix="+" className="Home_statNumber__GYegH" />
              <span className="Home_statLabel__wjI1z">Occupations</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="section section-alt">
      <div className="container">
        <div className="Home_sectionHeader__9bK15">
          <span className="section-label">Your Career Journey in 4 Steps</span>
          <h2>Simple, Transparent Process</h2>
        </div>
        <div className="Home_stepRow__EhfPv">
          <div className="Home_step__FEmcQ">
            <div className="Home_stepNumber__aCE+F">01</div>
            <Calendar size={32} className="Home_stepIcon__jr6bT" />
            <h4>Book a Session</h4>
            <p>Schedule your pre-counselling conversation.</p>
          </div>
          <div className="Home_stepConnector__noEW5"></div>
          <div className="Home_step__FEmcQ">
            <div className="Home_stepNumber__aCE+F">02</div>
            <Users size={32} className="Home_stepIcon__jr6bT" />
            <h4>Take the Assessment</h4>
            <p>Complete the online psychometric battery.</p>
          </div>
          <div className="Home_stepConnector__noEW5"></div>
          <div className="Home_step__FEmcQ">
            <div className="Home_stepNumber__aCE+F">03</div>
            <FileText size={32} className="Home_stepIcon__jr6bT" />
            <h4>Receive Your Report</h4>
            <p>Get your 20+ page detailed analysis.</p>
          </div>
          <div className="Home_stepConnector__noEW5"></div>
          <div className="Home_step__FEmcQ">
            <div className="Home_stepNumber__aCE+F">04</div>
            <PhoneCall size={32} className="Home_stepIcon__jr6bT" />
            <h4>Plan Your Future</h4>
            <p>1-on-1 debrief to create an actionable roadmap.</p>
          </div>
        </div>
        <div className="Home_servicesCta__3faxd" style={{ marginTop: '3rem' }}>
          <Link to="/how-it-works" className="Home_serviceLink__mjKQt">
            Why Career Counselling Matters →
          </Link>
        </div>
      </div>
    </section>

    <section className="Home_ctaBanner__wyQHd">
      <div className="container Home_ctaContainer__Xa1hL">
        <h2>Your Career Clarity Starts Here</h2>
        <p>
          Are you interested in Psychometric Career Assessments based Career Counselling? Click below to Explore Your True Potential.
        </p>
        <div className="Home_heroButtons__KQY4j" style={{ justifyContent: 'center', marginTop: '2rem' }}>
          <a
            href="https://vitaecareer.edumilestones.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Explore Your True Potential →
          </a>
          <a href="tel:+919335879877" className="btn-ghost">
            Call +91 9335879877
          </a>
        </div>
      </div>
    </section>
  </motion.div>
);

export default Home;